import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * MarkdownContent — theme-aware. Uses semantic classes that adapt to
 * both dark workspace (Chat) and light editorial (Library, Profile).
 * All color choices use `[data-theme="cream"] .xxx` overrides in index.css.
 */
const MarkdownContent = ({ content }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      h1: ({ children }) => (
        <h1 className="font-display text-xl font-semibold mt-5 mb-3 text-white tracking-tight [data-theme=cream]_&:text-cream-900">
          {children}
        </h1>
      ),
      h2: ({ children }) => (
        <h2 className="font-display text-lg font-semibold mt-4 mb-2.5 text-white tracking-tight">
          {children}
        </h2>
      ),
      h3: ({ children }) => (
        <h3 className="font-display text-base font-semibold mt-3 mb-2 text-white">
          {children}
        </h3>
      ),
      p: ({ children }) => (
        <p className="mb-3 leading-relaxed text-[inherit] text-sm">{children}</p>
      ),
      ul: ({ children }) => (
        <ul className="list-disc list-inside mb-3 space-y-1.5 text-[inherit] text-sm marker:text-atelier-coral">
          {children}
        </ul>
      ),
      ol: ({ children }) => (
        <ol className="list-decimal list-inside mb-3 space-y-1.5 text-[inherit] text-sm marker:text-atelier-coral">
          {children}
        </ol>
      ),
      li: ({ children }) => <li className="ml-2">{children}</li>,
      strong: ({ children }) => (
        <strong className="font-semibold text-white">{children}</strong>
      ),
      em: ({ children }) => <em className="italic text-obsidian-200">{children}</em>,
      code: ({ inline, children }) =>
        inline ? (
          <code className="bg-atelier-surface2 px-1.5 py-0.5 rounded text-xs font-mono text-atelier-coral border border-atelier-line">
            {children}
          </code>
        ) : (
          <code className="block bg-atelier-surface2 p-3 rounded-xl text-xs font-mono overflow-x-auto mb-3 border border-atelier-line text-atelier-ink">
            {children}
          </code>
        ),
      pre: ({ children }) => (
        <pre className="bg-atelier-surface2 p-3 rounded-xl overflow-x-auto mb-3 border border-atelier-line">
          {children}
        </pre>
      ),
      a: ({ href, children }) => (
        <a
          href={href}
          className="text-atelier-coral hover:text-atelier-coralHi underline underline-offset-2 transition-colors"
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </a>
      ),
      blockquote: ({ children }) => (
        <blockquote className="border-l-2 border-atelier-coral pl-4 italic my-3 text-obsidian-200 text-sm">
          {children}
        </blockquote>
      ),
      hr: () => <hr className="my-5 border-obsidian-600" />,
      table: ({ children }) => (
        <div className="overflow-x-auto my-3 rounded-lg border border-obsidian-600">
          <table className="min-w-full border-collapse text-sm">{children}</table>
        </div>
      ),
      thead: ({ children }) => <thead className="bg-white/[0.03]">{children}</thead>,
      tr: ({ children }) => (
        <tr className="border-b border-obsidian-600 last:border-0">{children}</tr>
      ),
      th: ({ children }) => (
        <th className="px-3 py-2 text-left label-micro text-obsidian-100 whitespace-nowrap">
          {children}
        </th>
      ),
      td: ({ children }) => <td className="px-3 py-2 text-sm text-obsidian-100">{children}</td>,
    }}
  >
    {content}
  </ReactMarkdown>
);

export default MarkdownContent;
